import { lazy, Suspense, useEffect } from 'react'
import { Redirect, Route, Switch } from 'wouter'
import { useLaunchParams, useMainButton, useBackButton } from '@tma.js/sdk-react'
import { Clock, ShieldX, UserX } from 'lucide-react'

import { Layout } from '@/components/Layout'
import { useAuthStore } from '@/stores/authStore'
import { closeMiniApp } from '@/lib/telegram'
import { HomePage } from '@/pages/Home'

const DialogsPage = lazy(() => import('@/pages/Dialogs').then((module) => ({ default: module.DialogsPage })))
const ApprovalsPage = lazy(() => import('@/pages/Approvals').then((module) => ({ default: module.ApprovalsPage })))
const TeamPage = lazy(() => import('@/pages/Team').then((module) => ({ default: module.TeamPage })))
const AnalyticsPage = lazy(() => import('@/pages/Analytics').then((module) => ({ default: module.AnalyticsPage })))
const SettingsPage = lazy(() => import('@/pages/Settings').then((module) => ({ default: module.SettingsPage })))

export function App() {
  const launchParams = useLaunchParams()
  const mainButton = useMainButton()
  const backButton = useBackButton()
  const { authenticate, isAuthenticated, isLoading, accessStatus } = useAuthStore()

  useEffect(() => {
    if (launchParams.initDataRaw) {
      authenticate(launchParams.initDataRaw)
    }
  }, [launchParams.initDataRaw, authenticate])

  useEffect(() => {
    mainButton.hide()
    backButton.show()
  }, [mainButton, backButton])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-tg-bg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tg-button" />
      </div>
    )
  }

  if (accessStatus === 'pending') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-tg-bg p-6 animate-fade-in">
        <div className="w-20 h-20 rounded-full bg-tg-button/10 flex items-center justify-center mb-6">
          <Clock className="w-10 h-10 text-tg-button" />
        </div>
        <h1 className="text-xl font-semibold text-tg-text mb-3 text-center">
          Запрос отправлен
        </h1>
        <p className="text-tg-hint text-center text-sm leading-relaxed max-w-[280px] mb-8">
          Ваш запрос на доступ отправлен администратору.
          Вы получите уведомление, когда он будет рассмотрен.
        </p>
        <button
          onClick={closeMiniApp}
          className="px-6 py-3 rounded-xl bg-tg-section text-tg-text text-sm font-medium active:scale-95 transition-transform"
        >
          Закрыть
        </button>
      </div>
    )
  }

  if (accessStatus === 'rejected') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-tg-bg p-6 animate-fade-in">
        <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mb-6">
          <ShieldX className="w-10 h-10 text-red-500" />
        </div>
        <h1 className="text-xl font-semibold text-tg-text mb-3 text-center">
          Доступ отклонён
        </h1>
        <p className="text-tg-hint text-center text-sm leading-relaxed max-w-[280px] mb-8">
          К сожалению, ваш запрос на доступ был отклонён.
          Обратитесь к администратору для уточнения.
        </p>
        <button
          onClick={closeMiniApp}
          className="px-6 py-3 rounded-xl bg-tg-section text-tg-text text-sm font-medium active:scale-95 transition-transform"
        >
          Закрыть
        </button>
      </div>
    )
  }

  if (accessStatus === 'deactivated') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-tg-bg p-6 animate-fade-in">
        <div className="w-20 h-20 rounded-full bg-orange-500/10 flex items-center justify-center mb-6">
          <UserX className="w-10 h-10 text-orange-500" />
        </div>
        <h1 className="text-xl font-semibold text-tg-text mb-3 text-center">
          Аккаунт деактивирован
        </h1>
        <p className="text-tg-hint text-center text-sm leading-relaxed max-w-[280px] mb-8">
          Ваш аккаунт был деактивирован.
          Обратитесь к администратору.
        </p>
        <button
          onClick={closeMiniApp}
          className="px-6 py-3 rounded-xl bg-tg-section text-tg-text text-sm font-medium active:scale-95 transition-transform"
        >
          Закрыть
        </button>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-tg-bg p-6 animate-fade-in">
        <div className="w-20 h-20 rounded-full bg-tg-button/10 flex items-center justify-center mb-6">
          <ShieldX className="w-10 h-10 text-tg-hint" />
        </div>
        <h1 className="text-xl font-semibold text-tg-text mb-3 text-center">
          AI Agent Platform
        </h1>
        <p className="text-tg-hint text-center text-sm leading-relaxed max-w-[280px]">
          Для работы с приложением необходимо открыть его через Telegram
        </p>
      </div>
    )
  }

  return (
    <Layout>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Switch>
          <Route path="/" component={HomePage} />
          <Route path="/dialogs" component={DialogsPage} />
          <Route path="/approvals" component={ApprovalsPage} />
          <Route path="/team" component={TeamPage} />
          <Route path="/analytics" component={AnalyticsPage} />
          <Route path="/settings" component={SettingsPage} />
          <Route>
            <Redirect to="/" replace />
          </Route>
        </Switch>
      </Suspense>
    </Layout>
  )
}

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-[240px] items-center justify-center">
      <div className="h-7 w-7 animate-spin rounded-full border-2 border-tg-button border-t-transparent" />
    </div>
  )
}
